// One Analyses row (DESIGN_SPEC §11.3, §12.17; RESTYLE §5): a focusable <tr> holding a real link (Cmd- and
// middle-click open a tab); clicking anywhere else on the row or pressing Enter opens the report.
//   <JobRow eff selected {...RowTree} queuePos? profileOffered? parentName? query onSelect onOpen onFold onFocusRow register observe />
//     verdict · state | sample (name, 8…8 hash, url / input on / matching labels, children toggle or "child of" note) |
//     strongest signal | environment | ran | submitted
//   verdict · state: the filled verdict banner with its score · LIVE · QUEUED + place · wait · failed, cancelled and
//     completed-without-verdict in ink · a run on a node past its timeout keeps LIVE (no ping) and says "overdue"
//   submitted: HH:MM, or "Oct 13 01:46" when the row sits in another day's group (a child under its parent)
//   tree (family.ts): children hang off their parent on hairline guides; "2 children" folds them (ArrowRight / ArrowLeft
//     on the row unfold / fold, ArrowLeft on a child goes to its parent)
//   Live rows tick once a second (run clock, queue wait); every other row renders only when its data changes.
import { useQuery } from "@tanstack/react-query";
import { memo, useEffect, useRef, type CSSProperties, type KeyboardEvent, type MouseEvent } from "react";
import { Link } from "react-router-dom";
import { JobState, LiveBadge, MicroTag, MT, QueuedBadge, Sep, Time, VerdictBanner } from "@/components/ui";
import { jobQueryOptions, useClock } from "@/hooks";
import { hash8, plural, utc } from "@/lib/format";
import { jobName, urlScheme } from "@/lib/records";
import { signatureOf, tagNames } from "@/features/submit/verdictFields";
import { rowView, type Eff, type StateCell } from "./listState";
import { StrongestSignal } from "./StrongestSignal";

/** Where the row sits in its family (family.ts TreeItem, flattened so the memo compares values). */
type RowTree = {
  depth: number;
  last: boolean;
  rails: string;
  kids: number;
  shownKids: number;
  liveKids: number;
  maliciousKids: number;
  folded: boolean;
  /** space-separated row ids of the shown children */
  childIds: string;
  under?: string;
  orphanOf?: string;
};

type JobRowProps = RowTree & {
  eff: Eff;
  selected: boolean;
  queuePos?: number;
  profileOffered?: boolean;
  /** The name of the parent (`under` or `orphanOf`) when it is loaded (else it is fetched for an orphan). */
  parentName?: string;
  /** The search text: labels it matches are shown. */
  query: string;
  onSelect: (id: string) => void;
  onOpen: (id: string) => void;
  onFold: (id: string, fold: boolean) => void;
  /** Select and focus another row (ArrowLeft from a child to its parent). */
  onFocusRow: (id: string) => void;
  register: (id: string, el: HTMLTableRowElement | null) => void;
  observe: (el: HTMLElement, id: string) => () => void;
};

export const rowId = (id: string) => `run-${id}`;

/** Environment stacks profile over network in the two-line rows of 900–1279 px. */
const STACK = "min-[900px]:max-[1279px]:block min-[900px]:max-[1279px]:truncate";

/** Sticky first column while the table scrolls sideways (under 900 px); opaque so nothing ghosts under it. */
export const STICKY = "max-[899px]:sticky max-[899px]:left-0 max-[899px]:z-[2] max-[899px]:bg-paper";

/** Every verdict banner in the column is one width, so the scores line up. */
const BANNER = "w-[128px] [&>.vb-w]:flex-1";

/** Guides: hairlines in ink-ghost, 16 px per level, the vertical 4 px into its level (the .elbow recipe's geometry). */
const GUIDE = "pointer-events-none absolute border-ink-ghost";

/** The verdict · state cell (also Submit's Recent runs). */
export function StateView({ cell }: { cell: StateCell }) {
  if (cell.kind === "verdict") {
    return (
      <span className="grid justify-items-start gap-1">
        <VerdictBanner v={cell.label} score={cell.score} size="sm" className={BANNER} />
        {cell.why && <span className="meta leading-none">{cell.why}</span>}
      </span>
    );
  }
  if (cell.state === "running" && cell.overdue)
    return (
      <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
        <LiveBadge size="sm" ping={false} title="On a node past its timeout: the record has not caught up, or the node has not collected" />
        <span className="meta leading-none">overdue</span>
      </span>
    );
  if (cell.state === "running") return <LiveBadge size="sm" title="Running now: its stream or record says the guest is up" />;
  if (cell.state === "queued") {
    return (
      <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <QueuedBadge size="sm" />
        {(cell.note || cell.wait) && (
          <span className="meta">
            {cell.note}
            {cell.note && cell.wait && " · "}
            {cell.wait}
          </span>
        )}
      </span>
    );
  }
  return <JobState s={cell.state} />;
}

function ChildArrow() {
  return (
    <svg className="size-[9px] shrink-0 self-center" viewBox="0 0 10 10" aria-hidden="true">
      <path d="M2 1v5.5h6.5M6.2 4.2l2.3 2.3-2.3 2.3" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ChildNote({ parentId, name }: { parentId: string; name?: string }) {
  const { data } = useQuery({ ...jobQueryOptions(parentId), enabled: !name, staleTime: Infinity });
  const label = name ?? (data ? jobName(data) : `${parentId.slice(0, 8)}…`);
  return (
    <span className="mt-1 flex min-w-0 items-baseline gap-1.5 text-meta text-ink-mute">
      <ChildArrow />
      <span className="shrink-0">child of</span>
      <Link to={`/jobs/${parentId}`} tabIndex={-1} viewTransition className="lnk min-w-0 truncate" title={label} translate="no">
        {label}
      </Link>
    </span>
  );
}

/** Ancestor rails (│) and this row's own guide below its elbow (├) run the full cell height; the td clips them. */
function Rails({ depth, last, rails }: { depth: number; last: boolean; rails: string }) {
  const lines: number[] = [];
  for (let level = 1; level < depth; level++) if (rails[level - 1] === "1") lines.push(level);
  if (!last) lines.push(depth);
  return (
    <>
      {lines.map((level) => (
        <span key={level} aria-hidden="true" className={`${GUIDE} -top-[200px] -bottom-[200px] border-l`} style={{ left: `${(level - 1 - depth) * 16 + 4}px` }} />
      ))}
    </>
  );
}

type FoldProps = Pick<RowTree, "kids" | "shownKids" | "liveKids" | "maliciousKids" | "folded" | "childIds"> & { id: string; onFold: (id: string, fold: boolean) => void };

/** "▸ 2 children"; folded, it still says what it hides ("· 1 live", "· 1 malicious"). */
function FoldToggle({ id, kids, shownKids, liveKids, maliciousKids, folded, childIds, onFold }: FoldProps) {
  if (!kids) return null;
  const word = plural(kids, "child", "children");
  if (!shownKids) return <span className="meta whitespace-nowrap">{`${kids} ${word}, filtered out`}</span>;
  return (
    <button
      type="button"
      className="copy-text self-center whitespace-nowrap max-[899px]:self-start"
      aria-expanded={!folded}
      aria-controls={childIds}
      title={folded ? "Show the runs queued from this run's dropped files" : "Fold the child runs"}
      onClick={() => onFold(id, !folded)}
    >
      <span className="caret" aria-hidden="true" />
      {shownKids === kids ? `${kids} ${word}` : `${shownKids} of ${kids} ${word}`}
      {folded && liveKids > 0 && <span className="text-live-text">· {liveKids} live</span>}
      {folded && maliciousKids > 0 && <span className="text-critical-text">· {maliciousKids} malicious</span>}
    </button>
  );
}

function shownLabels(labels: readonly string[] | undefined, query: string): string[] {
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (!labels?.length || !terms.length) return [];
  return labels.filter((l) => terms.some((t) => l.toLowerCase().includes(t)));
}

function openTab(href: string) {
  window.open(href, "_blank", "noopener");
}

export const JobRow = memo(function JobRow({
  eff,
  selected,
  queuePos,
  profileOffered,
  parentName,
  query,
  depth,
  last,
  rails,
  kids,
  shownKids,
  liveKids,
  maliciousKids,
  folded,
  childIds,
  under,
  orphanOf,
  onSelect,
  onOpen,
  onFold,
  onFocusRow,
  register,
  observe,
}: JobRowProps) {
  const now = useClock(1000, eff.live);
  const view = rowView(eff, { now, queuePos, profileOffered });
  const j = eff.job;
  const href = `/jobs/${j.id}`;
  const name = jobName(j);
  // middle truncation keeps the extension (or the end of a URL); a short name never needs it
  const tail = j.kind === "url" ? 16 : 10;
  const scheme = urlScheme(j);
  const tags = [...(scheme ? [scheme] : []), ...(j.allow_input ? ["input on"] : []), ...shownLabels(j.labels, query)];
  const ref = useRef<HTMLTableRowElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    register(j.id, el);
    const off = observe(el, j.id);
    return () => {
      off();
      register(j.id, null);
    };
  }, [j.id, register, observe]);

  const onClick = (e: MouseEvent<HTMLTableRowElement>) => {
    if ((e.target as Element).closest("a, button")) return; // links and the fold toggle act themselves
    if (window.getSelection()?.toString()) return; // selecting text, not opening
    if (e.metaKey || e.ctrlKey) openTab(href);
    else onOpen(j.id);
  };
  const onAuxClick = (e: MouseEvent<HTMLTableRowElement>) => {
    if (e.button !== 1 || (e.target as Element).closest("a")) return;
    e.preventDefault();
    openTab(href);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLTableRowElement>) => {
    if (e.target !== e.currentTarget || e.altKey || e.metaKey || e.ctrlKey || e.shiftKey) {
      if (e.key === "Enter" && e.target === e.currentTarget && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        openTab(href);
      }
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      onOpen(j.id);
    } else if (e.key === "ArrowRight" && shownKids > 0 && folded) {
      e.preventDefault();
      onFold(j.id, false);
    } else if (e.key === "ArrowLeft") {
      if (shownKids > 0 && !folded) onFold(j.id, true);
      else if (under) onFocusRow(under);
      else return;
      e.preventDefault();
    }
  };

  const ran = view.ran;
  const nested = depth > 0;
  const toggle = (
    <FoldToggle id={j.id} kids={kids} shownKids={shownKids} liveKids={liveKids} maliciousKids={maliciousKids} folded={folded} childIds={childIds} onFold={onFold} />
  );
  return (
    <tr
      ref={ref}
      id={rowId(j.id)}
      tabIndex={0}
      aria-selected={selected ? "true" : undefined}
      className="cursor-pointer [&>td]:py-1.5"
      onFocus={(e) => e.target === e.currentTarget && onSelect(j.id)}
      onClick={onClick}
      onAuxClick={onAuxClick}
      onKeyDown={onKeyDown}
    >
      <td className={STICKY}>
        <StateView cell={view.state} />
      </td>
      <td className="overflow-hidden">
        <div className="relative min-w-0 ml-[calc(var(--depth)*16px)]" style={{ "--depth": depth } as CSSProperties}>
          {nested && <Rails depth={depth} last={last} rails={rails} />}
          <div className="flex min-w-0 flex-wrap items-baseline gap-x-2.5 gap-y-0.5 max-[899px]:flex-col max-[899px]:items-start">
            <Link to={href} tabIndex={-1} viewTransition className="rowlink name relative inline-flex min-w-0 max-w-full">
              {nested && <span aria-hidden="true" className={`${GUIDE} right-[calc(100%+3px)] bottom-1/2 h-[200px] w-[9px] border-b border-l`} />}
              {name.length > tail + 2 ? (
                <MT value={name} tail={tail} />
              ) : (
                <span className="truncate" title={name} translate="no">
                  {name}
                </span>
              )}
            </Link>
            {nested && parentName && <span className="sr-only">, child of {parentName}</span>}
            {(j.sample_sha || tags.length > 0) && (
              <span className="inline-flex min-w-0 items-baseline gap-2">
                {j.sample_sha && (
                  <span className="hash8" translate="no" title={j.sample_sha}>
                    {hash8(j.sample_sha)}
                  </span>
                )}
                {tags.map((t) => (
                  <MicroTag key={t} className="self-center">
                    {t}
                  </MicroTag>
                ))}
              </span>
            )}
            {/* its own line on phones, where the table scrolls and the line after the hash is cut off */}
            {toggle}
          </div>
          {orphanOf && <ChildNote parentId={orphanOf} name={parentName} />}
          {j.submitter?.name && (
            <span className="block text-meta text-ink-faint" translate="no">
              by {j.submitter.name}
            </span>
          )}
          {shownKids > 0 && !folded && <span aria-hidden="true" className={`${GUIDE} left-1 top-[calc(100%+3px)] h-[200px] border-l`} />}
        </div>
      </td>
      <td className="min-[900px]:overflow-hidden">
        <StrongestSignal cell={view.signal} />
      </td>
      <td className="max-[1199px]:hidden overflow-hidden" data-none={signatureOf(j) ? undefined : ""} title={signatureOf(j)} translate="no">
        {signatureOf(j) ? (
          <SignatureChip value={signatureOf(j)!} />
        ) : (
          <span className="truncate inline-block max-w-full">—</span>
        )}
      </td>
      <td className="max-[1359px]:hidden overflow-hidden" data-none={tagNames(j).length === 0 ? "" : undefined}>
        <RowTags tags={tagNames(j)} />
      </td>
      {/* a long pair ("linux-test · isolated") wraps at the separator rather than losing the network */}
      <td className="env max-[719px]:hidden min-[1280px]:whitespace-normal" translate="no">
        <span className={`${STACK} whitespace-nowrap`}>{j.profile}</span>
        <Sep className="min-[900px]:max-[1279px]:hidden" />
        <wbr />
        <span className={`${STACK} whitespace-nowrap`}>{j.network}</span>
      </td>
      <td className="r" data-none={ran ? undefined : ""} title={ran?.title}>
        {ran ? (
          <>
            {ran.text}
            {ran.of && <span className="text-meta text-ink-faint"> / {ran.of}</span>}
          </>
        ) : (
          "—"
        )}
      </td>
      {/* mirrors the first column inset, so the focus ring never touches the time */}
      <td className="r pr-2.5">
        <Time value={j.submitted_at}>
          <span className="whitespace-nowrap">{utc(j.submitted_at, "date-time")}</span>
        </Time>
      </td>
    </tr>
  );
});

const stopRow = (e: MouseEvent) => e.stopPropagation();

function RowTags({ tags }: { tags: readonly string[] }) {
  if (!tags.length) return <>—</>;
  const shown = tags.slice(0, 2);
  const extra = tags.length - shown.length;
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      {shown.map((t) => (
        <MicroTag key={t} href={`/jobs?q=${encodeURIComponent(t)}`} title={`Filter Analyses by tag ${t}`} onClick={stopRow}>
          {t}
        </MicroTag>
      ))}
      {extra > 0 && (
        <span className="text-meta text-ink-faint" title={tags.slice(2).join(", ")}>
          +{extra}
        </span>
      )}
    </span>
  );
}

/** Clickable family-signature chip: navigates to /jobs?q=<family> so the
 *  Analyses view's existing text filter (which already searches tags +
 *  signature + labels) narrows to this family. stopPropagation keeps
 *  the parent <tr>'s row-click (opens the report) from also firing. */
function SignatureChip({ value }: { value: string }) {
  return (
    <MicroTag href={`/jobs?q=${encodeURIComponent(value)}`} title={`Filter Analyses by ${value}`} onClick={stopRow} className="max-w-full">
      {value}
    </MicroTag>
  );
}
